/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import SettingsPanel, { isSettingsRoute } from './SettingsPanel';
import LiveSessionCallModal from './LiveSessionCallModal';
import { Menu } from 'lucide-react';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { useLiveSessionContext } from '../context/LiveSessionContext';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL } from '../constants/entityLabels';
import { getBrandName } from '../utils/scope';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const location = useLocation();
  const { currentLogoUrl, user, accountName } = useAuth();

  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);
  const {
    sessions: liveSessions,
    joinedRoom: liveSessionJoinedRoom,
    connectionStatus: liveSessionConnectionStatus,
    micEnabled: liveSessionMicEnabled,
    camEnabled: liveSessionCamEnabled,
    recordingStatus: liveSessionRecordingStatus,
    recordingElapsedMs: liveSessionRecordingElapsedMs,
    recordingSupported: liveSessionRecordingSupported,
    leave: leaveLiveSession,
    toggleMic: toggleLiveSessionMic,
    toggleCam: toggleLiveSessionCam,
    startRecording: startLiveSessionRecording,
    stopRecording: stopLiveSessionRecording,
    attachLocalVideo: attachLiveSessionLocalVideo,
    attachRemoteVideo: attachLiveSessionRemoteVideo,
    remoteVideoOn: liveSessionRemoteVideoOn,
    annotationsSupported: liveSessionAnnotationsSupported,
    getVideoContentPoint: getLiveSessionVideoContentPoint,
    sendAnnotationBubble: sendLiveSessionAnnotationBubble,
    sendAnnotationCursor: sendLiveSessionAnnotationCursor,
    hideAnnotationCursor: hideLiveSessionAnnotationCursor,
  } = useLiveSessionContext();

  // Close mobile sidebar automatically on desktop viewport resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const joinedLiveSession = liveSessions.find((s) => s.room === liveSessionJoinedRoom);
  const remoteLiveSessionName = joinedLiveSession?.creator_name
    || joinedLiveSession?.device_name
    || null;

  const isSubAccountContext = !!user?.subAccountUID;
  const mobileLogoSrc = currentLogoUrl || logo;
  const mobileLogoAlt = currentLogoUrl ? `${isSubAccountContext ? SUB_ACCOUNT_LABEL.singular : ACCOUNT_LABEL.singular} Logo` : 'Send2Me Logo';
  const brandName = getBrandName(user, accountName);
  const inSettings = isSettingsRoute(location.pathname);

  return (
    <div className="flex flex-col lg:flex-row h-screen overflow-hidden lg:p-4 lg:gap-4 bg-slate-950/5">
      {/* Mobile Header */}
      <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-primary text-white shadow-md z-30 shrink-0">
        <div className="flex items-center gap-3 min-w-0 flex-1 mr-2">
          <div className="w-10 h-10 bg-white shadow-md shadow-sky-500/20 rounded-xl flex items-center justify-center p-1.5 shrink-0">
            <img
              src={mobileLogoSrc}
              alt={mobileLogoAlt}
              className="w-auto max-w-[70px] h-[30px] max-h-[30px] object-contain"
              onError={(e) => { e.currentTarget.src = logo; e.currentTarget.onerror = null; }}
            />
          </div>
          <span className="font-bold text-lg sm:text-xl tracking-tight truncate" title={brandName}>
            {brandName}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={handleToggleSidebar}
            aria-label="Open navigation menu"
            aria-expanded={sidebarOpen}
            className="relative p-2.5 hover:bg-white/10 active:bg-white/20 rounded-xl transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/40"
          >
            <Menu size={24} />
            {liveSessions.length > 0 && (
              <span className="absolute top-1.5 right-1.5 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-error" />
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Sidebar Container */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={handleCloseSidebar}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      />

      {/* Secondary Settings Navigation Panel */}
      {inSettings && (
        <SettingsPanel onNavigateMobile={handleCloseSidebar} />
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto lg:glass-panel lg:rounded-xl p-2 min-w-0">
        <div className="">
          <Outlet />
        </div>
      </main>

      <LiveSessionCallModal
        room={liveSessionJoinedRoom}
        connectionStatus={liveSessionConnectionStatus}
        micEnabled={liveSessionMicEnabled}
        camEnabled={liveSessionCamEnabled}
        recordingStatus={liveSessionRecordingStatus}
        recordingElapsedMs={liveSessionRecordingElapsedMs}
        recordingSupported={liveSessionRecordingSupported}
        localName="You"
        remoteName={remoteLiveSessionName}
        onToggleMic={toggleLiveSessionMic}
        onToggleCam={toggleLiveSessionCam}
        onStartRecording={startLiveSessionRecording}
        onStopRecording={stopLiveSessionRecording}
        onLeave={leaveLiveSession}
        attachLocalVideo={attachLiveSessionLocalVideo}
        attachRemoteVideo={attachLiveSessionRemoteVideo}
        annotationsSupported={liveSessionAnnotationsSupported}
        canAnnotate={liveSessionAnnotationsSupported && !!liveSessionJoinedRoom && liveSessionRemoteVideoOn}
        getVideoContentPoint={getLiveSessionVideoContentPoint}
        onAnnotationBubble={sendLiveSessionAnnotationBubble}
        onAnnotationCursor={sendLiveSessionAnnotationCursor}
        onHideAnnotationCursor={hideLiveSessionAnnotationCursor}
      />
    </div>
  );
};

export default Layout;
